import React, { useContext } from 'react'
import NextImage from 'next/image'
import LazyLoad from 'react-lazyload'
import { analytics } from '@helpers/analytics'
import { getLink, getCurrencyCode } from '@helpers/env'
import { getProductCategory } from '@helpers/products'
import { resizeShopifyImage } from '@helpers/shopify'
import PriceComparison from '@components/ui/PriceComparison'
import CollectionTags from '@components/collection/CollectionTags'
import { CollectionProduct as TypeCollectionProduct } from 'types/collection'
import { ProductOption, ProductImage } from 'types/product'
import CollectionContext from '../collectionContext'
import styles from './styles.module.scss'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

type ProductDetailsProps = {
  product: TypeCollectionProduct
}
function ProductDetails({ product }: ProductDetailsProps) {
  const isTabletOrUp = useMediaQuery({
    minWidth: BREAKPOINT_SIZES.TABLET,
    initialState: true
  })

  return (
    <div className={styles.ProductDetails}>
      <div>
        <h5 className={styles.product__productType}>{product.product_type}</h5>

        <h5 className={styles.product__title}>{product.title}</h5>
      </div>

      {product.product_type !== 'Gift Card' && (
        <div className={styles.product__prices}>
          <PriceComparison
            price={product.price}
            fullPrice={product.compare_at_price}
            size={isTabletOrUp ? 'medium' : 'small'}
          />
        </div>
      )}
    </div>
  )
}

function getProductListImageIndex(
  productCategory: string | null,
  productType: string
): number {
  const matchesProductCategory = /Trousers|Shorts|Denim/.test(
    productCategory || ''
  )

  const isNotSwimsProductType = !/Swims/.test(productType)

  if (matchesProductCategory && isNotSwimsProductType) {
    return 2
  }

  return 1
}

type PrimaryImageProperties = {
  images: ProductImage[]
  productOptions: ProductOption[]
  productType: string
}

function plpPrimaryImage({
  images,
  productOptions,
  productType
}: PrimaryImageProperties) {
  const productCategory = getProductCategory(productOptions)

  return images[getProductListImageIndex(productCategory, productType)]
}

type CollectionProductProps = {
  product: TypeCollectionProduct
}

const CollectionProduct = ({ product }: CollectionProductProps) => {
  const { collectionHandle } = useContext(CollectionContext)

  const productLink = getLink(`/products/${product.handle}`)

  const images = product.images

  const handleProductClick = () => {
    analytics('Product Clicked', {
      list_id: collectionHandle,
      context: 'Collection',
      category: getProductCategory(product.options),
      quantity: 1,
      name: product.product_type,
      color: product.title,
      price: parseFloat(product.price),
      url: productLink,
      image_url: product.image,
      brand: 'SPOKE',
      currency: getCurrencyCode()
    })
  }

  const primaryImage = plpPrimaryImage({
    images,
    productOptions: product.options,
    productType: product.product_type
  })

  return (
    <a
      className={styles.product}
      href={productLink}
      onClick={handleProductClick}
    >
      <CollectionTags tags={product.tags} />
      <div
        className={`${styles.product__imageContainer} ${styles.imagePlaceholder}`}
      >
        <div className={styles.product__image}>
          <LazyLoad once>
            {primaryImage && (
              <NextImage
                className={`${styles.product__primaryImage} lazy`}
                src={resizeShopifyImage((primaryImage || {}).src || '', {
                  width: '479',
                  height: '600',
                  crop: 'center'
                })}
                alt={`${product.title} ${product.product_type} Primary`}
                width="479"
                height="600"
              />
            )}
            <NextImage
              className={`${styles.product__secondaryImage} lazy`}
              src={resizeShopifyImage((images[0] || {}).src || '', {
                width: '479',
                height: '600',
                crop: 'center'
              })}
              alt={`${product.title} ${product.product_type} Secondary`}
              width="479"
              height="600"
            />
          </LazyLoad>
        </div>
      </div>
      <ProductDetails product={product} />
    </a>
  )
}

export default CollectionProduct
